// src/components/Shared/ConsoleModal.jsx
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const TAG_STYLES = {
  info: 'text-[#55FFFF]',
  scan: 'text-[#FFAA00]',
  probe: 'text-[#9ca3af]',
  crack: 'text-[#f0abfc]',
  open: 'text-[#2ed573] bg-[#2ed573]/15',
  warn: 'text-[#f87171]',
  success: 'text-[#07150c] bg-[#2ed573] !px-1.5 font-extrabold',
};

const ConsoleModal = ({
  title = "RCON КОНСОЛЬ",
  lines = [],
  isLoading = false,
  status = "Idle",
  statusColor = "text-gray-400",
  actions = [],
  onClose,
  maxWidth = 'max-w-4xl',
  error,
  isConsoleInputEnabled = false,
  onSendCommand
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';
  const { showToast } = useNotification();

  const [cmdInput, setCmdInput] = useState('');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [sessionSeconds, setSessionSeconds] = useState(0);
  const [clearedBeforeIndex, setClearedBeforeIndex] = useState(-1);

  const logBodyRef = useRef(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  // Session stopwatch
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setSessionSeconds(s => s + 1);
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);


  // Filter lines if user clicked "Clear"
  const visibleLines = useMemo(() => {
    if (clearedBeforeIndex < 0) return lines;
    return lines.slice(clearedBeforeIndex);
  }, [lines, clearedBeforeIndex]);

  // Auto-scroll on new logs
  useEffect(() => {
    if (logBodyRef.current) {
      logBodyRef.current.scrollTop = logBodyRef.current.scrollHeight;
    }
  }, [visibleLines]);

  // Auto-focus input when input is enabled
  useEffect(() => {
    if (isConsoleInputEnabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isConsoleInputEnabled]);

  // Parse clean host name for the prompt
  const cleanHost = useMemo(() => {
    const raw = title || '';
    const match = raw.match(/([a-zA-Z0-9.-]+):(\d+)/);
    if (match) {
      return match[1].split('.')[0] || 'server';
    }
    return 'server';
  }, [title]);

  // Parse server address and port from title/status
  const serverInfo = useMemo(() => {
    const raw = `${title} ${status}`;
    const match = raw.match(/([a-zA-Z0-9.-]+):(\d+)/);
    if (match) {
      return { host: match[1], port: match[2] };
    }
    return { host: cleanHost, port: '25575' };
  }, [title, status, cleanHost]);

  // Submit command
  const handleCommandSubmit = useCallback((e) => {
    e?.preventDefault();
    if (!cmdInput.trim() || !onSendCommand) return;
    const cmd = cmdInput.trim();
    setHistory(prev => [...prev, cmd]);
    setHistoryIndex(-1);
    onSendCommand(cmd);
    setCmdInput('');
  }, [cmdInput, onSendCommand]);

  // Up/Down arrows for command history
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (history.length === 0) return;
      const nextIdx = historyIndex === -1 ? history.length - 1 : Math.max(0, historyIndex - 1);
      setHistoryIndex(nextIdx);
      setCmdInput(history[nextIdx] || '');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex === -1) return;
      const nextIdx = historyIndex + 1;
      if (nextIdx >= history.length) {
        setHistoryIndex(-1);
        setCmdInput('');
      } else {
        setHistoryIndex(nextIdx);
        setCmdInput(history[nextIdx] || '');
      }
    }
  };

  // Copy logs
  const handleCopyLogs = useCallback(() => {
    const text = visibleLines.map(l => {
      const ts = l.timestamp || '';
      const t = l.type ? `[${l.type.toUpperCase()}]` : '';
      return `${ts} ${t} ${l.text || ''}`.trim();
    }).join('\n');

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        showToast(isEn ? 'Console logs copied' : 'Логи консоли скопированы');
      });
    }
  }, [visibleLines, isEn, showToast]);

  // Clear logs
  const handleClearLogs = useCallback(() => {
    setClearedBeforeIndex(lines.length);
    showToast(isEn ? 'Console display cleared' : 'Экран консоли очищен');
  }, [lines.length, isEn, showToast]);

  // Parse tag type and badge style from line
  const parseLineDetails = (line) => {
    let tag = '[SYSTEM]';
    let tagClass = 'info';
    const rawText = line.text || '';

    if (line.type === 'command') {
      tag = '[RCON]';
      tagClass = 'crack';
    } else if (line.type === 'response') {
      tag = '[OUTPUT]';
      tagClass = 'probe';
    } else if (line.type === 'error' || rawText.includes('[ERR]') || rawText.toLowerCase().includes('failed') || rawText.toLowerCase().includes('error') || rawText.toLowerCase().includes('пофикшена') || rawText.toLowerCase().includes('timed out')) {
      tag = '[ERROR]';
      tagClass = 'warn';
    } else if (line.type === 'success' || rawText.includes('[OK]') || rawText.toLowerCase().includes('connected') || rawText.toLowerCase().includes('успешн')) {
      tag = '[SUCCESS]';
      tagClass = 'open';
    } else if (line.type === 'warning') {
      tag = '[WARNING]';
      tagClass = 'scan';
    } else {
      tag = '[SYSTEM]';
      tagClass = 'info';
    }

    // Clean bracket tags from beginning of text
    let cleanText = rawText.replace(/^\[(SYS|OK|ERR|RCON|OUTPUT|SYSTEM|ERROR|SUCCESS)\]\s*/i, '');
    return { tag, tagClass, cleanText };
  };

  const isConnected = isConsoleInputEnabled && !isLoading;
  const isError = Boolean(error || status?.toLowerCase().includes('fail') || status?.toLowerCase().includes('error'));

  return (
    <div className="w-full mx-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.98, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.98, y: 8 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="terminal-card relative border border-white/[0.12] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.95)] !min-h-[580px] sm:!min-h-[620px] flex flex-col bg-[#090a0f] rounded-[22px] overflow-hidden select-text text-left"
      >
        {/* ═══ Window Header (macOS Dots + Title + Tools) ═══ */}
        <div className="terminal-header shrink-0 flex items-center justify-between px-5 py-3.5 bg-[#111319] border-b border-white/[0.08]">
          <div className="terminal-left-wrap flex items-center gap-3.5 min-w-0">
            <div className="terminal-window-dots flex items-center gap-2">
              <span className="t-dot red cursor-pointer hover:opacity-80 transition-opacity" onClick={onClose} title={isEn ? "Close" : "Закрыть"} />
              <span className="t-dot yellow cursor-pointer hover:opacity-80 transition-opacity" onClick={handleClearLogs} title={isEn ? "Clear" : "Очистить"} />
              <span className="t-dot green" />
            </div>
            <div className="terminal-title-text flex items-center gap-2 truncate font-mono text-[0.82rem] font-bold text-white tracking-wider uppercase">
              <span className={`w-2 h-2 rounded-full shrink-0 ${isConnected ? 'bg-[#2ed573] shadow-[0_0_8px_#2ed573]' : isLoading ? 'bg-[#ffbd2e] animate-pulse' : 'bg-red-400'}`} />
              <span className="truncate">{title}</span>
            </div>
          </div>

          <div className="terminal-header-tools flex items-center gap-2 shrink-0">
            <button
              type="button"
              className="t-tool-btn"
              onClick={handleClearLogs}
              title={isEn ? 'Clear logs' : 'Очистить экран'}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              <span>{isEn ? 'Clear' : 'Очистить'}</span>
            </button>
            <button
              type="button"
              className="t-tool-btn"
              onClick={handleCopyLogs}
              title={isEn ? 'Copy logs' : 'Копировать текст'}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              <span>{isEn ? 'Copy' : 'Копировать'}</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-7 h-7 rounded-full bg-white/[0.06] hover:bg-white/[0.14] text-white/70 hover:text-white flex items-center justify-center transition-colors ml-1"
              title={isEn ? 'Close' : 'Закрыть'}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* ═══ Realtime Metrics Bar (Exact Match to RconAudit) ═══ */}
        <div className="terminal-metrics-bar grid grid-cols-2 sm:grid-cols-4 bg-[#0d0f15] border-b border-white/[0.06] px-5 py-2.5 gap-3 shrink-0 text-left">
          <div className="t-metric-item flex flex-col gap-0.5">
            <span className="t-metric-label text-[0.68rem] text-white/40 uppercase font-bold tracking-wider">
              {isEn ? 'Status' : 'Статус'}
            </span>
            <span className={`t-metric-value font-mono text-[0.88rem] font-bold truncate ${isConnected ? 'text-[#2ed573]' : isLoading ? 'text-amber-400' : 'text-red-400'}`}>
              {status || (isConnected ? (isEn ? 'ONLINE' : 'В СЕТИ') : isLoading ? (isEn ? 'CONNECTING...' : 'ПОДКЛЮЧЕНИЕ...') : (isEn ? 'DISCONNECTED' : 'ОТКЛЮЧЕНО'))}
            </span>
          </div>

          <div className="t-metric-item flex flex-col gap-0.5">
            <span className="t-metric-label text-[0.68rem] text-white/40 uppercase font-bold tracking-wider">
              {isEn ? 'Target Host' : 'Целевой хост'}
            </span>
            <span className="t-metric-value font-mono text-[0.88rem] font-bold text-white truncate">
              {serverInfo.host || '—'}
            </span>
          </div>

          <div className="t-metric-item flex flex-col gap-0.5">
            <span className="t-metric-label text-[0.68rem] text-white/40 uppercase font-bold tracking-wider">
              {isEn ? 'RCON Port' : 'Порт RCON'}
            </span>
            <span className="t-metric-value font-mono text-[0.88rem] font-bold text-[#f0abfc] truncate">
              {serverInfo.port || '—'}
            </span>
          </div>

          <div className="t-metric-item flex flex-col gap-0.5">
            <span className="t-metric-label text-[0.68rem] text-white/40 uppercase font-bold tracking-wider">
              {isEn ? 'Session Time' : 'Время сессии'}
            </span>
            <span className="t-metric-value font-mono text-[0.88rem] font-bold text-white/90">
              {formatDuration(sessionSeconds)}
            </span>
          </div>
        </div>

        {/* ═══ Terminal Body with Colored Badges and Formatting ═══ */}
        <div
          ref={logBodyRef}
          className="terminal-body flex-1 min-h-[380px] max-h-[500px] overflow-y-auto px-5 py-4 bg-[#07080b] font-mono text-[0.82rem] leading-[1.7] flex flex-col gap-1.5 select-text text-left"
          style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
        >
          {visibleLines.length === 0 ? (
            <div className="text-white/35 italic py-6 text-center select-none font-sans text-sm">
              {isEn ? 'Awaiting incoming log messages...' : 'Ожидание логов соединения...'}
            </div>
          ) : (
            visibleLines.map((line, index) => {
              const { tag, tagClass, cleanText } = parseLineDetails(line);
              const timeStr = line.timestamp || `[00:00:00]`;

              return (
                <div key={index} className="log-line flex items-start gap-2.5 word-break-break-word select-text" style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
                  <span className="log-time text-[#6b7280] shrink-0 font-mono text-[0.82rem] select-text" style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
                    {timeStr}
                  </span>
                  <span className={`log-tag ${TAG_STYLES[tagClass] || 'text-[#55FFFF]'} font-bold px-1 py-0 rounded text-[0.82rem] shrink-0 uppercase select-text`} style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
                    {tag}
                  </span>
                  <span className={`log-text ${line.type === 'command' ? 'text-[#f0abfc] font-bold' : line.type === 'error' ? 'text-red-400 font-medium' : line.type === 'success' ? 'text-[#2ed573]' : 'text-[#f5f5f7]'} flex-1 leading-relaxed text-[0.82rem] select-text`} style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
                    {cleanText}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* ═══ Interactive Shell Command Prompt (Exact Match to RconAudit) ═══ */}
        <div className="interactive-shell-box border-t border-white/[0.08] bg-[#0d0f15] px-5 py-3.5 shrink-0">
          {!isConnected ? (
            <div className="shell-lock-overlay flex items-center justify-between gap-3 text-white/40 text-[0.80rem] font-mono">
              <span className="shell-lock-title flex items-center gap-2 text-white/60">
                <span className="text-base">{isLoading ? '⏳' : isError ? '❌' : '🔒'}</span>
                <span>
                  {isLoading
                    ? (isEn ? 'Establishing secure RCON tunnel to target server...' : 'Установка защищённого RCON-туннеля к серверу...')
                    : isError
                    ? (error || (isEn ? 'Connection terminated or refused.' : 'Соединение прервано или отклонено.'))
                    : (isEn ? 'Interactive RCON console will unlock once connected.' : 'Интерактивная RCON-консоль будет разблокирована после подключения.')}
                </span>
              </span>
              {actions && actions.length > 0 && (
                <div className="flex items-center gap-2">
                  {actions.map((act, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={act.onClick}
                      disabled={act.disabled}
                      className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${act.primary ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30' : 'bg-white/10 text-white hover:bg-white/20'}`}
                    >
                      {act.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <form className="shell-input-form flex items-center gap-2.5" onSubmit={handleCommandSubmit}>
              <span className="shell-prompt font-mono text-[0.84rem] font-bold text-[#f0abfc] shrink-0 select-none">
                rcon@{cleanHost}:~$
              </span>
              <input
                ref={inputRef}
                type="text"
                className="shell-input flex-1 bg-[#151821] border border-white/[0.12] rounded-xl h-[38px] px-3.5 text-white font-mono text-[0.84rem] outline-none focus:border-[#c026d3] focus:bg-[#08090d] transition-all"
                value={cmdInput}
                onChange={(e) => setCmdInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isEn ? 'Enter command (help, list, whitelist, op, say)...' : 'Введите команду (help, list, whitelist, op, say)...'}
                autoComplete="off"
              />
              <button
                type="submit"
                className="shell-send-btn !h-[38px] !px-4 !bg-[#c026d3] hover:!bg-[#d946ef] !text-black !font-bold rounded-full inline-flex items-center gap-1.5 transition-all shrink-0 cursor-pointer shadow-[0_0_15px_rgba(192,38,211,0.35)]"
                style={{ color: '#000000' }}
              >
                <span className="!text-black text-[0.82rem] font-bold" style={{ color: '#000000' }}>
                  {isEn ? 'Execute' : 'Выполнить'}
                </span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2.6" className="!stroke-black">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>

              {/* Disconnect button beside command prompt */}
              {actions && actions.length > 0 && actions[0] && (
                <button
                  type="button"
                  onClick={actions[0].onClick}
                  title={actions[0].label}
                  className="h-[38px] px-3.5 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/25 text-xs font-bold transition-colors inline-flex items-center gap-1.5 shrink-0"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                    <line x1="12" y1="2" x2="12" y2="12" />
                  </svg>
                  <span>{actions[0].label}</span>
                </button>
              )}
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ConsoleModal;